import { useTranslation } from 'react-i18next';

import { fetchOrganizationFileLink } from '@/api/v4/organization-files.api';
import { fetchSpotlightOrganizationFileLink } from '@/api/v4/spotlight.api';
import { queryClient, QueryKey } from '@/config/query-client';
import type {
  DraftFile,
  File,
  FileLinkDownloadHandler,
  FileModeType,
} from '@/features/action-panel/files/file.interface';
import { FilesTabContent } from '@/features/action-panel/files/files-tab-content';
import type { UploadFileInputProps } from '@/features/action-panel/files/automatic-upload-file-button';
import { companyIdState } from '@/features/companies/overview/company.state';
import { downloadLinkAutomatically } from '@/helpers/download-link-automatically';
import { modalState } from '@/state/modal.state';
import { notify } from '@/ui/snackbar/notify';

import { useCompanyFileScanningStatus } from './use-company-file-scanning-status.query';
import { useCompanyFiles } from './use-company-files.query';
import { useShareCompanyFile } from './use-share-company-file.mutation';
import { useUploadCompanyFile } from './use-upload-company-file.mutation';
import {
  type CompanyFetchMode,
  useCompanyFetchMode,
  useIsSpotlightCompanyView,
} from '../../company-fetch-context';
import { useAtomValue, useSetAtom } from 'jotai';

const companyFileLinkFetchers: Record<
  CompanyFetchMode,
  typeof fetchOrganizationFileLink
> = {
  general: fetchOrganizationFileLink,
  spotlight: fetchSpotlightOrganizationFileLink,
};

export const CompanyFilesTab = () => {
  const { t } = useTranslation('default');
  const companyId = useAtomValue(companyIdState);
  const fetchMode = useCompanyFetchMode();
  const isReadOnly = useIsSpotlightCompanyView();
  const { uploadCompanyFile } = useUploadCompanyFile();
  const { shareCompanyFile } = useShareCompanyFile();
  const { data } = useCompanyFiles();

  const files = data ?? [];

  const setModal = useSetAtom(modalState);

  if (!companyId) return null;

  const openDeleteModal = (id: string, name: string) => {
    setModal({
      state: 'deleteCompanyFile',
      file: { id, name },
      companyId,
    });
  };

  const handleSubmitFiles: UploadFileInputProps['onSubmit'] = async files => {
    if (!companyId) return;

    setDraftFiles(files);

    try {
      await uploadCompanyFile(files);
    } catch (error) {
      if (error instanceof Error) {
        notify({ message: error.message });
      }
    }
  };

  const setDraftFiles = (uploadingFiles: globalThis.File[]) => {
    queryClient.setQueryData(
      [QueryKey.CompanyFiles, fetchMode, companyId],
      (files: Array<File | DraftFile> | undefined) => {
        if (!files) return;

        const draftFiles: DraftFile[] = uploadingFiles.map(file => ({
          id: file.name,
          name: file.name,
          createdDate: new Date().toISOString(),
          scanningStatus: null,
          creator: null,
          public: true,
          lastEditedDate: null,
        }));
        return [...draftFiles, ...files];
      },
    );
  };

  const handleFileLinkDownload: FileLinkDownloadHandler = async (
    file: File,
    mode: FileModeType,
  ) => {
    if (!companyId) return;

    const link = await companyFileLinkFetchers[fetchMode](
      {
        fileId: file.id,
        organizationId: companyId,
      },
      {
        mode,
      },
    );

    await downloadLinkAutomatically(link, file.name);
  };

  return (
    <FilesTabContent
      files={files}
      onFileLinkDownload={handleFileLinkDownload}
      emptyListLabel={t('actionPanel.files.empty', { context: 'company' })}
      onSubmit={handleSubmitFiles}
      onClickDelete={openDeleteModal}
      onShareFile={async payload => {
        await shareCompanyFile(payload);
      }}
      readOnly={isReadOnly}
      useFileScanningStatus={useCompanyFileScanningStatus}
    />
  );
};
